import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";

import useFetch from "frontend/utils/useFetch";
import usePost from "frontend/utils/usePost";
import useRefetchOnVisible from "frontend/utils/useRefetchOnVisible";
import { isAuthenticated, isGuest, getAccessToken } from "frontend/utils/authenticateHelper.js";

import { boardSize, rackSize, swapPassPenalty } from "frontend/utils/constants.js";
import { ActionCableConsumer } from "frontend/utils/actionCableProvider";

import PlayerScoreArea from "frontend/components/PlayerScoreArea";
import MoveHistorySidebar from "frontend/components/MoveHistorySidebar";
import TileDistributionModal from "frontend/components/TileDistributionModal";
import Modal from "frontend/components/Modal";
import ConfirmationModal from "frontend/components/ConfirmationModal";
import ErrorModal from "frontend/components/ErrorModal";
import MoreMenu from "frontend/components/MoreMenu";
import Board from "frontend/components/Board";
import TileRack from "frontend/components/TileRack";
import ExchangeView from "frontend/components/ExchangeView";
import LandscapeOverlay from "frontend/components/LandscapeOverlay";

import passIcon from "frontend/assets/icons/icon-pass.png";
import swapIcon from "frontend/assets/icons/icon-swap.png";
import recallIcon from "frontend/assets/icons/icon-recall.png";

import "../../../../scss/game_container.scss";

const initalBoardValues = Array.from({ length: boardSize }, () =>
  Array.from({ length: boardSize }, () => 0)
);

const initalRackValues = Array.from({ length: rackSize }, () => 0);

const Show = ({
  match: {
    params: { id }
  },
  onSignupClick
}) => {
  const history = useHistory();
  const [boardValues, setBoardValues] = useState(initalBoardValues);
  const [tempBoardValues, setTempBoardValues] = useState(initalBoardValues);
  const [lastMoveInfo, setLastMoveInfo] = useState(null);
  const [gameFlowData, setGameFlowData] = useState({});
  const [playerData, setPlayerData] = useState({});
  const [rackValues, setRackValues] = useState(initalRackValues);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [moves, setMoves] = useState([]);
  const [exchanging, setExchanging] = useState(false);
  const [showTileDistribution, setShowTileDistribution] = useState(false);
  const [showMoveHistory, setShowMoveHistory] = useState(false);
  const [showPassConfirm, setShowPassConfirm] = useState(false);
  const [showResignConfirm, setShowResignConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const is_authenticated = isAuthenticated();

  const { isFetching, hasFetched, fetchError, doFetch } = useFetch(
    `/api/v1/games/${id}`,
    ({ response, json }) => {
      var status = response.status;
      if (status != 200) {
        setErrorMessage("Unable to load game. Redirecting...");
        setTimeout(() => history.replace(`/`), 2000);
        return;
      }

      var game = json.game;
      var moves = json.moves;

      var board = game.board;
      var you = game.you;
      var them = game.them;
      var last_move = game.last_move;
      var available_tiles = game.available_tiles;
      var your_rack = game.your_rack;
      var your_turn = game.your_turn;
      var your_score = game.your_score;
      var their_score = game.their_score;
      var allow_swap = game.allow_swap;
      var complete = game.complete;
      var your_result = game.your_result;
      var stage = game.stage;

      setBoardValues(board);
      setRackValues(your_rack);
      setTempBoardValues(initalBoardValues);
      setGameFlowData({
        id: id,
        your_turn: your_turn,
        your_score: your_score,
        their_score: their_score,
        allow_swap: allow_swap,
        complete: complete,
        your_result: your_result,
        available_tiles: available_tiles,
        vs_computer: game.vs_computer,
        stage: stage,
        last_move: last_move,
      });
      setPlayerData({
        you: you,
        them: them
      });
      setMoves(moves);
      setLastMoveInfo(
        last_move &&
          last_move.row_num &&
          last_move.row_num.reduce((map, row, index) => {
            map[row] = map[row]
              ? map[row].concat(last_move.col_num[index])
              : [last_move.col_num[index]];
            return map;
          }, {})
      );

    }
  );

  const placeTiles = () => {
    var row_num = [];
    var col_num = [];
    var tile_value = [];

    tempBoardValues.forEach((arr, row) => {
      arr.forEach((value, col) => {
        if (value != 0) {
          row_num.push(row);
          col_num.push(col);
          tile_value.push(value);
        }
      });
    });

    return {
      row_num: row_num,
      col_num: col_num,
      tile_value: tile_value
    };
  };

  const { isPosting, hasPosted, postError, doPost } = usePost(
    "/api/v1/moves",
    ({ response, json }) => {
      var status = response.status;
      if (status === 422) {
        // Unprocessable entity - show the validation message
        setErrorMessage(json?.message || "Invalid move. Please try again.");
      } else if (status != 200) {
        // Other errors - generic message
        setErrorMessage("Something went wrong. Please try again.");
      }
    }
  );

  useEffect(() => {
    if (!is_authenticated) {
      history.replace('/games/how-to-play');
      return;
    }
    doFetch();
  }, [is_authenticated, id, history]);

  useEffect(() => {
    const handleOrientationChange = () => {
      // iOS Safari caches container query dimensions on orientation change.
      // Force a reflow on the game container after the browser settles so
      // cqw/cqh recalculate against the updated portrait dimensions.
      setTimeout(() => {
        const container = document.querySelector('.game-content-wrapper');
        if (!container) return;
        container.style.display = 'none';
        void container.offsetHeight;
        container.style.display = '';
      }, 300);
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  useRefetchOnVisible(doFetch, is_authenticated);

  const handleRackSet = (col, value) => {
    var newRack = rackValues.slice();
    newRack[col] = value;
    setRackValues(newRack);
  };

  const handleBoardSet = (row, col, value) => {
    var newBoard = tempBoardValues.map(function(arr) {
      return arr.slice();
    });
    newBoard[row][col] = value;
    setTempBoardValues(newBoard);
  };

  const recallTiles = () => {
    const tilesToRecall = [];
    const newTempBoard = tempBoardValues.map(arr => arr.slice());

    // Collect tiles from temp board and clear them
    newTempBoard.forEach((row, rowIdx) => {
      row.forEach((value, colIdx) => {
        if (value !== 0) {
          tilesToRecall.push(value);
          newTempBoard[rowIdx][colIdx] = 0;
        }
      });
    });

    // Return tiles to empty rack slots
    const newRack = rackValues.slice();
    for (let i = 0; i < newRack.length && tilesToRecall.length > 0; i++) {
      if (newRack[i] === 0) {
        newRack[i] = tilesToRecall.shift();
      }
    }

    setTempBoardValues(newTempBoard);
    setRackValues(newRack);
  };

  const postTilePlacement = () => {
    doPost({
      move_info: {
        game_id: id,
        move_type: "tile_placement",
        ...placeTiles()
      }
    });
  };

  const postPass = () => {
    setShowPassConfirm(false);
    doPost({
      move_info: {
        game_id: id,
        move_type: "pass"
      }
    });
  };

  const postResign = () => {
    setShowResignConfirm(false);
    fetch(`/api/v1/games/${id}`, {
      headers: {
        AUTHORIZATION: `Bearer ${getAccessToken()}`,
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      credentials: "same-origin",
      method: "PATCH",
      body: JSON.stringify({ forfit: true })
    }).then(() => {
      doFetch();
    });
  };

  const postExchange = (returned_tiles, callback) => {
    doPost(
      {
        move_info: {
          game_id: id,
          move_type: "swap",
          returned_tiles: returned_tiles
        }
      },
      () => {
        callback();
      }
    );
  };

  const handleReceivedUpdate = () => {
    if (!isFetching) {
      doFetch();
    }
  };

  if (!hasFetched) {
    return (
      <div className="game-container">
        <div className="game-panel">
          <div className="floating-card" style={{ height: "600px" }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <PlayerScoreArea
        yourTurn={gameFlowData.your_turn}
        playerUsername={playerData.you?.username}
        opponentUsername={playerData.them?.username}
        playerScore={gameFlowData.your_score}
        opponentScore={gameFlowData.their_score}
        tilesRemaining={gameFlowData.available_tiles?.length || 0}
        stage={gameFlowData.stage}
        complete={gameFlowData.complete}
        yourResult={gameFlowData.your_result}
        vsComputer={gameFlowData.vs_computer}
        lastMove={gameFlowData.last_move}
      />

      {gameFlowData.complete && isGuest() && !nudgeDismissed && (
        <div className="guest-nudge">
          <p className="guest-nudge-text">
            Sign up to keep your games and challenge other players.
          </p>
          <div className="guest-nudge-actions">
            <button className="guest-nudge-cta" onClick={onSignupClick}>
              Save my progress
            </button>
            <button
              className="guest-nudge-dismiss"
              onClick={() => setNudgeDismissed(true)}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <div className="game-content-wrapper">
          <div className="game-panel">
            <Board
              boardValues={boardValues}
              tempBoardValues={tempBoardValues}
              lastMoveInfo={lastMoveInfo}
              handleBoardSet={handleBoardSet}
            />
          </div>
        </div>

        <div className="game-controls">
          <div className="tile-ledge">
            <TileRack rackValues={rackValues} handleRackSet={handleRackSet} />
          </div>
          <div className="options-bar">
            <MoreMenu
              gameComplete={gameFlowData.complete}
              onResign={() => setShowResignConfirm(true)}
              onShowTileDistribution={() => setShowTileDistribution(true)}
              onShowMoveHistory={() => setShowMoveHistory(true)}
            />

            <button
              className="option-btn"
              onClick={() => setShowPassConfirm(true)}
              disabled={!gameFlowData.your_turn || gameFlowData.complete}
            >
              <img src={passIcon} className="icon" alt="" aria-hidden="true" />
              <span className="label">Pass</span>
            </button>

            <button
              className="play-btn"
              onClick={postTilePlacement}
              disabled={!gameFlowData.your_turn || gameFlowData.complete}
            >
              PLAY!
            </button>

            <button
              className="option-btn"
              onClick={() => {
                recallTiles();
                setExchanging(true);
              }}
              disabled={!gameFlowData.your_turn || gameFlowData.complete || !gameFlowData.allow_swap}
            >
              <img src={swapIcon} className="icon" alt="" aria-hidden="true" />
              <span className="label">Swap</span>
            </button>

            <button
              className="option-btn"
              onClick={recallTiles}
              disabled={gameFlowData.complete}
            >
              <img src={recallIcon} className="icon" alt="" aria-hidden="true" />
              <span className="label">Recall</span>
            </button>
          </div>
        </div>
      </DndProvider>

      {showMoveHistory && (
        <Modal title="Move History" onClose={() => setShowMoveHistory(false)} maxWidth="400px">
          <MoveHistorySidebar
            moves={moves}
            currentUsername={playerData.you?.username}
          />
        </Modal>
      )}

      {exchanging && (
        <Modal title="Swap Tiles" onClose={() => setExchanging(false)} maxWidth="500px">
          <ExchangeView
            rackValues={rackValues}
            postExchange={postExchange}
            cancel={() => setExchanging(false)}
          />
        </Modal>
      )}

      {showTileDistribution && (
        <TileDistributionModal
          availableTiles={gameFlowData.available_tiles}
          onClose={() => setShowTileDistribution(false)}
        />
      )}

      {showPassConfirm && (
        <ConfirmationModal
          title="Pass Turn"
          message={<>Are you sure you want to pass your turn?<br />This adds <strong>{swapPassPenalty} pts</strong> to your score.</>}
          confirmText="Pass"
          cancelText="Cancel"
          onConfirm={postPass}
          onCancel={() => setShowPassConfirm(false)}
        />
      )}

      {showResignConfirm && (
        <ConfirmationModal
          title="Resign"
          message="Are you sure you want to resign? This will end the game and count as a loss."
          confirmText="Resign"
          cancelText="Cancel"
          onConfirm={postResign}
          onCancel={() => setShowResignConfirm(false)}
          variant="danger"
        />
      )}

      {errorMessage && (
        <ErrorModal
          title="Invalid Move"
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <ActionCableConsumer
        channel={{ channel: "GamesChannel" }}
        onReceived={handleReceivedUpdate}
      />

      <LandscapeOverlay />
    </div>
  );
};

export default Show;
